// src/ui/Type.tsx (T1): the type parts (design system A). The eyebrow is a small label in the meta color over a
// heading; the kicker is the smaller role and group label. Root classes: hb-eyebrow, hb-kicker, hb-sec-head, hb-stat.
import type { ComponentChildren } from "preact";
import { cx } from "./cx";

export function Eyebrow(props: { as?: "p" | "span" | "h2" | "h3"; light?: boolean; class?: string; id?: string; children?: ComponentChildren }) {
  const Tag = props.as ?? "p";
  return (
    <Tag id={props.id} class={cx("hb-eyebrow", "eyebrow", props.light && "eyebrow-light", props.class)}>
      {props.children}
    </Tag>
  );
}

export function Kicker(props: { as?: "p" | "span"; class?: string; children?: ComponentChildren }) {
  const Tag = props.as ?? "span";
  return <Tag class={cx("hb-kicker", "kicker", props.class)}>{props.children}</Tag>;
}

/**
 * A section head: eyebrow, Title Case heading, one short sub line, actions on the right.
 * Keep `sub` to one line: the owner rejects paragraphs in cards (put caveats in an InfoTip).
 */
export function SectionHead(props: {
  eyebrow?: ComponentChildren;
  title: ComponentChildren;
  sub?: ComponentChildren;
  actions?: ComponentChildren;
  level?: 1 | 2 | 3;
  id?: string;
  class?: string;
}) {
  const H = (`h${props.level ?? 2}`) as "h1" | "h2" | "h3";
  return (
    <div class={cx("hb-sec-head", "sec-head", props.class)}>
      <div>
        {props.eyebrow ? <Eyebrow>{props.eyebrow}</Eyebrow> : null}
        <H class="h-sec" id={props.id}>
          {props.title}
        </H>
        {props.sub ? <p class="sub">{props.sub}</p> : null}
      </div>
      {props.actions ? <div class="actions">{props.actions}</div> : null}
    </div>
  );
}

/** A numeral with a short label: "7/10 Rooms started". */
export function Stat(props: { value: ComponentChildren; of?: number | string; label: ComponentChildren; class?: string }) {
  return (
    <div class={cx("hb-stat", "stat", props.class)}>
      <b class="num">
        {props.value}
        {props.of !== undefined ? <small>/{props.of}</small> : null}
      </b>
      <span>{props.label}</span>
    </div>
  );
}
